/**
 * Shared bits for the Pelora Billing (Phase 22) agency screens: flag gate, permissions,
 * the Simulated badge, responsive row cells and deadline colours.
 */
import React from 'react';
import { useFeature } from '../../../hooks/useFeature';
import { useAuth } from '../../../context/authcontext';
import { isOwner } from '../../../utils/baaAccess';
import type { PermissionString } from '../../../types/permissions';

export const BILLING_SUBMIT = 'billing:submit' as PermissionString;
export const BILLING_WORK = 'billing:work' as PermissionString;
export const BILLING_CONFIG = 'billing:config' as PermissionString;
export const BILLING_CLAIMS_READ = 'billing:claims:read' as PermissionString;
export const EDI_ANY = [BILLING_SUBMIT, BILLING_WORK, BILLING_CONFIG] as PermissionString[];
/** Who may see a patient's eligibility card: claims readers plus any Pelora Billing role. */
export const ELIGIBILITY_VIEW = [BILLING_CLAIMS_READ, ...EDI_ANY] as PermissionString[];

/** The tenant's `billingEdi` flag (GET /users/me/permissions `features`). Off: nothing renders, no request. */
export function useEdiEnabled(): boolean {
  return useFeature('billingEdi');
}

/** Owner check for the send-mode switch (admin:owner permission or the owner Cognito group). Cosmetic; the API enforces it. */
export function useIsOwner(): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return isOwner(user) || (user.groups || []).some((g) => String(g).toLowerCase() === 'owner');
}

export function useCurrentUserId(): string | null {
  const { user } = useAuth();
  return user?.id || null;
}

/** Gate: renders children only when the tenant's billingEdi flag is on. */
export const EdiGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const enabled = useEdiEnabled();
  return enabled ? <>{children}</> : null;
};

export const SimulatedBadge: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    className={`inline-flex items-center rounded bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-800 ${className}`}
    title="Produced by the simulated payer; no claim reached a real payer"
  >
    Simulated
  </span>
);

export const ErrorNote: React.FC<{ message: string | null }> = ({ message }) =>
  message ? (
    <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      {message}
    </p>
  ) : null;

/** A cell that shows its column label on phones (rows become cards below the sm breakpoint). */
export const Cell: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({ label, className = '', children }) => (
  <div className={`min-w-0 break-words ${className}`}>
    <span className="mr-1 text-xs text-slate-500 sm:hidden">{label}:</span>
    {children}
  </div>
);

/** ≤5 days red, ≤15 amber, else neutral. */
export function deadlineTone(daysLeft: number): string {
  if (daysLeft <= 5) return 'border-red-300 bg-red-50 text-red-800';
  if (daysLeft <= 15) return 'border-amber-300 bg-amber-50 text-amber-900';
  return 'border-slate-200 bg-white text-slate-700';
}

export function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export const WORK_KIND_LABEL: Record<string, string> = {
  BLOCK: 'Block',
  REJECTION: 'Rejection',
  DENIAL: 'Denial',
  UNMATCHED: 'Unmatched payment',
  DEADLINE: 'Filing deadline',
  INFO_REQUEST: 'Information request',
};

export const WORK_STATE_LABEL: Record<string, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  WAITING_AGENCY: 'Waiting on agency',
  RESOLVED: 'Resolved',
  WRITTEN_OFF: 'Written off',
};
